import { View } from 'foundations-components/components/View';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { sortProductLines } from 'pricing-pages-lib/utils/sortingUtils';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useEffect, useState } from 'react';
import { MARKETING_PLUS_SUITE } from 'self-service-api/constants/BundleTypes';
import { BUNDLE } from 'self-service-api/constants/UpgradeProducts';
import BannerSection from '../components/banners/BannerSection';
import IntegrationsSection from '../components/common/IntegrationsSection';
import PhoneSupportFooter from '../components/common/PhoneSupportFooter';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import FailedLoadBanner from '../components/common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import ProductTile from '../components/merch-card/ProductTile';
import MobileBundleFeatureTable from '../components/mobile/MobileBundleFeatureTable';
import MobileProductLevelSelectorButtons from '../components/mobile/MobileProductLevelSelectorButtons';
import MobileWrapper from '../components/mobile/containers/MobileWrapper';
import { OWNERSHIP_CHECK_FAILED } from '../constants/ErrorCauses';
import { getMobileMerchCardsToShow } from '../constants/merchCards/MerchCardsToShow';
import { NavItemEnum } from '../constants/routing/NavItems';
import { SCREENS } from '../constants/tracking/Screens';
import { MARKETING_PLUS_PAGE_FEATURE_TABLE } from '../constants/tracking/TrackingSources';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useTrackRetail } from '../hooks/useTrackRetail';
import { useAppSelector } from '../reducers';
import { isUngatedForMarketingPlusSelector } from '../selectors/GatingSelectors';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { bundledProductLinesForForApiNameSelector } from '../selectors/ProductConfigsSelectors';
import { bundleProductsForBundleTypeSelector } from '../selectors/ProductsSelectors';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtils';
import ErrorPage from './ErrorPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MobileMarketingPlusPage = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle('marketing-plus');
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useAppSelector(hasOnlyOwnershipCheckFailedSelector);
  const productLevels = getMobileMerchCardsToShow(MARKETING_PLUS_SUITE);
  const [selectedProductLevel, setSelectedProductLevel] = useState(productLevels[0]);
  const screen = SCREENS.MARKETING_PLUS;
  const navItem = NavItemEnum.MARKETING_PLUS;
  const isUngatedForMarketingPlus = useAppSelector(isUngatedForMarketingPlusSelector);
  useRedirectIfIneligible({
    condition: () => !isUngatedForMarketingPlus
  });
  useEffect(() => {
    if (!isAppReady) return;
    trackPageView({
      screen
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppReady]);
  const highlightedProducts = useAppSelector(state => {
    if (hasPricingAndPurchasingFailed) return [];
    return bundleProductsForBundleTypeSelector(state, {
      bundleType: MARKETING_PLUS_SUITE
    }).map(({
      apiName
    }) => apiName);
  });
  const productLevelToHighlightedProductMap = productLevels.reduce((acc, productLevel, index) => {
    acc[productLevel] = highlightedProducts[index];
    return acc;
  }, {});
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const productLinesFromBundle = useAppSelector(state => {
    return sortProductLines(bundledProductLinesForForApiNameSelector(state, {
      apiName: highlightedProducts[0]
    }));
  });
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        errorCause: OWNERSHIP_CHECK_FAILED
      });
    }
  }
  if (!isAppReady) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  return /*#__PURE__*/_jsxs(MobileWrapper, {
    navItem: navItem,
    navMarkerName: "MARKETING_PLUS_PAGE",
    children: [/*#__PURE__*/_jsx(BannerSection, {
      screen: screen
    }), /*#__PURE__*/_jsx(FailedLoadBanner, {
      hasFailed: hasPricingAndPurchasingFailed
    }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
      children: [/*#__PURE__*/_jsx(ProductPageTitle, {
        nonProductIdentifier: navItem
      }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
        nonProductIdentifier: navItem
      })]
    }), /*#__PURE__*/_jsx(MobileProductLevelSelectorButtons, {
      productLevels: productLevels,
      onClick: setSelectedProductLevel,
      selectedProductLevel: selectedProductLevel
    }), productLevelToHighlightedProductMap[selectedProductLevel] && /*#__PURE__*/_jsxs(PageSection, {
      children: [/*#__PURE__*/_jsx(View, {
        marginBlockStart: "500",
        children: /*#__PURE__*/_jsx(CardGrid, {
          columns: 1,
          rowGap: "0",
          children: /*#__PURE__*/_jsx(ProductTile, {
            apiName: productLevelToHighlightedProductMap[selectedProductLevel],
            screen: screen,
            gridItemProps: {
              gridColumnStart: 1,
              paddingInline: '700',
              showBorderLeft: true,
              showBorderRight: true
            }
          })
        })
      }), /*#__PURE__*/_jsx(MobileBundleFeatureTable, {
        apiNames: highlightedProducts,
        productLevel: selectedProductLevel,
        productLinesFromBundle: productLinesFromBundle,
        screen: screen,
        trackingSource: MARKETING_PLUS_PAGE_FEATURE_TABLE
      })]
    }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(PhoneSupportFooter, {
      phoneSourceId: "marketingplus-footer",
      screen: screen,
      upgradeProduct: BUNDLE
    })]
  });
};
export default MobileMarketingPlusPage;